<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';

const emit = defineEmits(['create']);

const { t } = useI18n();
const router = useRouter();
const duplicates = ref([]);

const dialogRef = ref(null);
const contactsFormRef = ref(null);
const contact = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isCreatingContact = computed(() => uiFlags.value.isCreating);

const createNewContact = contactItem => {
  contact.value = contactItem;
  duplicates.value = [];
};

const handleDialogConfirm = async () => {
  if (!contact.value) return;
  emit('create', contact.value);
};

const showDuplicates = contacts => {
  duplicates.value = contacts;
};

const openExistingContact = async id => {
  dialogRef.value.close();
  await router.push({ name: 'contacts_edit', params: { contactId: id } });
};

const matchedFields = fields =>
  fields.map(field => t('CONTACTS_DUPLICATE.FIELDS.' + field)).join(', ');

const onSuccess = () => {
  duplicates.value = [];
  contactsFormRef.value?.resetForm();
  dialogRef.value.close();
};

const closeDialog = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef, contactsFormRef, onSuccess, showDuplicates });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    @confirm="handleDialogConfirm"
  >
    <section
      v-if="duplicates.length"
      role="alert"
      aria-live="polite"
      class="mb-4 rounded-lg border border-n-brand bg-n-alpha-1 p-4 text-n-slate-12"
    >
      <h3 class="font-semibold">
        {{
          t(
            duplicates.length > 1
              ? 'CONTACTS_DUPLICATE.CONFLICT_TITLE'
              : 'CONTACTS_DUPLICATE.TITLE'
          )
        }}
      </h3>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('CONTACTS_DUPLICATE.DESCRIPTION') }}
      </p>
      <ul class="mt-3 flex flex-col gap-3">
        <li v-for="existing in duplicates" :key="existing.id">
          <p class="font-medium">
            {{ existing.name }}
            {{ t('CONTACTS_DUPLICATE.REFERENCE', { id: existing.id }) }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ existing.email }} {{ existing.phone_number }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{
              t('CONTACTS_DUPLICATE.MATCHED', {
                fields: matchedFields(existing.matched_fields),
              })
            }}
          </p>
          <Button
            type="button"
            variant="link"
            :label="t('CONTACTS_DUPLICATE.OPEN')"
            @click="openExistingContact(existing.id)"
          />
        </li>
      </ul>
      <Button
        type="button"
        variant="link"
        class="mt-2"
        :label="t('CONTACTS_DUPLICATE.REVIEW')"
        @click="duplicates = []"
      />
    </section>
    <ContactsForm
      ref="contactsFormRef"
      is-new-contact
      @update="createNewContact"
    />
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand-text"
          @click="closeDialog"
        />
        <Button
          type="submit"
          :label="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SAVE_CONTACT')
          "
          color="blue"
          :disabled="contactsFormRef?.isFormInvalid"
          :is-loading="isCreatingContact"
        />
      </div>
    </template>
  </Dialog>
</template>
